import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

// Dynamic schema based on client_type
const buildSchema = (clientType) => {
  const base = {
    audience: z.enum(['current_client', 'prospective_client', 'professional', 'other']),
    client_type: z.enum(['business', 'individual']),
    services: z.array(z.string()).min(1, "Please select at least one service"),
    timeline: z.enum(['immediate', 'soon', 'planning', 'exploring']),
    values_fit: z.array(z.string()).min(1, "Please select at least one value"),
    story: z.string().min(20, "Please provide at least 20 characters").max(1000, "Max 1000 characters"),
    referral_source: z.enum(['google', 'linkedin', 'referral', 'event', 'existing_client', 'other']),
    referrer_name: z.string().max(150).optional().or(z.literal('')),
    consent: z.boolean().refine(val => val === true, "You must consent to be contacted"),
    hp_note: z.string().max(500).optional(),
    ts: z.number(),
    fp_hash: z.string().max(64),
  };

  if (clientType === 'business') {
    return z.object({
      ...base,
      industry: z.string().min(1, "Industry is required").max(100),
      annual_revenue: z.enum(['under_500k', '500k_1m', '1m_5m', '5m_10m', 'over_10m']),
      accounting_software: z.string().min(1, "Accounting software is required").max(100),
      tax_situation: z.string().optional(),
      tax_situation_other: z.string().optional(),
      filing_status: z.string().optional(),
      has_cpa: z.string().optional(),
    }).refine(d => d.referral_source !== 'referral' || (d.referrer_name && d.referrer_name.trim()), {
      message: "Referrer name required", path: ['referrer_name']
    });
  } else {
    return z.object({
      ...base,
      tax_situation: z.enum(['simple_w2', 'multiple_income', 'irs_issue', 'life_change', 'self_employed', 'other']),
      tax_situation_other: z.string().max(300).optional(),
      filing_status: z.enum(['single', 'married_joint', 'married_separate', 'head_of_household', 'widower']),
      has_cpa: z.enum(['yes', 'no', 'not_sure']),
      industry: z.string().optional(),
      annual_revenue: z.string().optional(),
      accounting_software: z.string().optional(),
    }).refine(d => d.referral_source !== 'referral' || (d.referrer_name && d.referrer_name.trim()), {
      message: "Referrer name required", path: ['referrer_name']
    }).refine(d => d.tax_situation !== 'other' || (d.tax_situation_other && d.tax_situation_other.trim()), {
      message: "Please describe your situation", path: ['tax_situation_other']
    });
  }
};

const BUSINESS_SERVICES = [
  { value: 'tax_planning', label: 'Tax Planning & Compliance' },
  { value: 'accounting', label: 'Accounting & Bookkeeping' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'irs_help', label: 'IRS Representation & Relief' },
  { value: 'entity_formation', label: 'Entity Formation (LLC / S-Corp / C-Corp)' },
  { value: 'valuation', label: 'Business Valuation & Growth' },
  { value: 'consulting_cfo', label: 'Strategic Consulting / CFO Advisory' },
  { value: 'audit', label: 'Audit & Assurance' },
];

const INDIVIDUAL_SERVICES = [
  { value: 'personal_tax_prep', label: 'Personal Tax Preparation' },
  { value: 'tax_planning', label: 'Tax Planning & Strategy' },
  { value: 'irs_help', label: 'IRS Help (notices, liens, levies, back taxes)' },
  { value: 'estate_trust', label: 'Estate & Trust Planning' },
];

const VALUES = [
  { value: 'integrity', label: 'Integrity' },
  { value: 'excellence', label: 'Excellence' },
  { value: 'innovation', label: 'Innovation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'transparency', label: 'Transparency' },
  { value: 'impact', label: 'Community Impact' },
];

const LeadFormStep3 = ({ leadId, firstName, onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [storyLength, setStoryLength] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [clientType, setClientType] = useState(null); // null until chosen

  // Build schema dynamically
  const schema = clientType ? buildSchema(clientType) : buildSchema('business');

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      audience: '', client_type: '', services: [], timeline: '', values_fit: [],
      story: '', referral_source: '', referrer_name: '', consent: false,
      hp_note: '', ts: Date.now(), fp_hash: '',
      industry: '', annual_revenue: '', accounting_software: '',
      tax_situation: '', tax_situation_other: '', filing_status: '', has_cpa: '',
    }
  });

  const audienceValue = watch('audience');
  const referralSourceValue = watch('referral_source');
  const storyValue = watch('story');
  const taxSituationValue = watch('tax_situation');

  useEffect(() => {
    setStoryLength(storyValue ? storyValue.length : 0);
  }, [storyValue]);

  useEffect(() => {
    const fp = `${navigator.userAgent}_${screen.width}x${screen.height}_${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
    const hash = fp.split('').reduce((a, c) => ((a << 5) - a) + c.charCodeAt(0), 0).toString(16);
    setValue('fp_hash', hash);
    setValue('ts', Date.now());
  }, [setValue]);

  // When client_type changes, reset services
  const handleClientTypeChange = (type) => {
    setClientType(type);
    setValue('client_type', type);
    setSelectedServices([]);
    setValue('services', []);
  };

  const handleServiceToggle = (service) => {
    const updated = selectedServices.includes(service)
      ? selectedServices.filter(s => s !== service)
      : [...selectedServices, service];
    setSelectedServices(updated);
    setValue('services', updated);
  };

  const handleValueToggle = (value) => {
    const updated = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    setSelectedValues(updated);
    setValue('values_fit', updated);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, lead_id: leadId })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSubmissionResult(result);
        setShowSuccess(true);
        toast.success(`Request #${result.request_number} submitted successfully!`);

        // Fire the lead-form conversion event for Google Ads / GTM.
        // - Gated by REACT_APP_LEAD_CONVERSION_TRACKING_ENABLED env var (hard kill switch — set to "false" to skip the dataLayer push entirely, e.g. during campaign breaks or compliance freezes).
        // - Soft pause: the actual Google Ads tag in GTM can be paused via the GTM dashboard without a redeploy. See PRD Session 37 for full debug docs.
        // - PII-safe: only the anonymous request_number is pushed; no name/email/phone.
        // - Idempotent: the success branch is reachable once per submission; backend deduplicates by lead_id.
        if (process.env.REACT_APP_LEAD_CONVERSION_TRACKING_ENABLED !== 'false') {
          try {
            if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
              window.dataLayer.push({
                event: 'lead_form_submit',
                conversion_id: result.request_number,
                form_location: 'contact_page',
                page_path: '/contact',
              });
            }
          } catch (e) {
            // Analytics must never break the success flow.
          }
        }

        onComplete(result);
      } else {
        const errorMsg = result.error?.message || result.detail?.error?.message || 'Submission failed';
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen
  if (showSuccess && submissionResult) {
    return (
      <div className="min-h-screen bg-navy py-16 px-4 flex items-center justify-center">
        <Card className="max-w-2xl w-full p-8 bg-ivory">
          <CardContent className="text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-playfair text-navy">Thank You, {firstName}!</h2>
            <div className="bg-white border-2 border-gold rounded-lg p-6">
              <div className="text-sm text-gray-600 uppercase tracking-wide mb-2">Your Request Number</div>
              <div className="text-4xl font-bold text-navy font-mono tracking-wider mb-2">
                #{submissionResult.request_number}
              </div>
              <div className="text-sm text-gray-600">Please reference this number in any correspondence</div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">{submissionResult.message}</p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 text-left">
              <div className="font-semibold text-navy mb-2">What happens next?</div>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>Our team is reviewing your request</li>
                <li>You'll receive a confirmation email shortly</li>
                <li>We'll contact you using the information provided</li>
              </ul>
            </div>
            <div className="pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Need immediate assistance?<br />
                <a data-testid="lead-form-step3-a-1" href="tel:+18445993355" className="text-gold hover:underline font-semibold">Call us at 1-844-599-3355</a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showClientTypeFields = ['current_client', 'prospective_client'].includes(audienceValue);
  const availableServices = clientType === 'individual' ? INDIVIDUAL_SERVICES : BUSINESS_SERVICES;

  return (
    <div className="min-h-screen bg-navy py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <Card className="p-8 bg-ivory">
          <CardHeader>
            <CardTitle className="text-3xl font-playfair text-navy mb-2">Tell Us About Your Needs</CardTitle>
            <p className="text-gray-600">Help us understand how we can best serve you.</p>
          </CardHeader>

          <CardContent className="space-y-8">
            {/* Progress */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-semibold">&#10003;</div>
                <span className="ml-2 text-sm font-medium text-green-600">Basic Info</span>
              </div>
              <div className="flex-1 h-0.5 bg-green-500 mx-4" />
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-semibold">&#10003;</div>
                <span className="ml-2 text-sm font-medium text-green-600">Verified</span>
              </div>
              <div className="flex-1 h-0.5 bg-green-500 mx-4" />
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gold text-white flex items-center justify-center font-semibold">3</div>
                <span className="ml-2 text-sm font-medium">Details</span>
              </div>
            </div>

            <form data-testid="lead-form-step3-form-1" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Honeypot */}
              <input data-testid="lead-form-step3-input-1" type="text" {...register('hp_note')} style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

              {/* Q1: Audience */}
              <div>
                <Label className="text-base font-semibold">I am a... *</Label>
                <RadioGroup onValueChange={(val) => setValue('audience', val)}>
                  <div className="space-y-2 mt-2">
                    {[
                      { value: 'current_client', label: 'Current Client' },
                      { value: 'prospective_client', label: 'Prospective Client' },
                      { value: 'professional', label: 'Professional / Peer' },
                      { value: 'other', label: 'Other' },
                    ].map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt.value} id={`aud_${opt.value}`} />
                        <label htmlFor={`aud_${opt.value}`} className="cursor-pointer">{opt.label}</label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
                {errors.audience && <p className="text-red-500 text-sm mt-1">{errors.audience.message}</p>}
              </div>

              {/* Q2: Client Type (only for clients) */}
              {showClientTypeFields && (
                <div>
                  <Label className="text-base font-semibold">Are you inquiring as a... *</Label>
                  <RadioGroup onValueChange={handleClientTypeChange}>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${clientType === 'business' ? 'border-gold bg-gold/5' : 'border-gray-200 hover:border-gray-300'}`}>
                        <RadioGroupItem value="business" id="ct_business" />
                        <label htmlFor="ct_business" className="cursor-pointer">
                          <div className="font-semibold">Business Owner</div>
                          <div className="text-sm text-gray-500">Business representative</div>
                        </label>
                      </div>
                      <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${clientType === 'individual' ? 'border-gold bg-gold/5' : 'border-gray-200 hover:border-gray-300'}`}>
                        <RadioGroupItem value="individual" id="ct_individual" />
                        <label htmlFor="ct_individual" className="cursor-pointer">
                          <div className="font-semibold">Individual / Family</div>
                          <div className="text-sm text-gray-500">Personal tax or IRS help</div>
                        </label>
                      </div>
                    </div>
                  </RadioGroup>
                  {errors.client_type && <p className="text-red-500 text-sm mt-1">{errors.client_type.message}</p>}
                </div>
              )}

              {/* If professional/other, default to business client_type */}
              {audienceValue && !showClientTypeFields && !clientType && (() => { handleClientTypeChange('business'); return null; })()}

              {/* ===== BUSINESS FIELDS ===== */}
              {clientType === 'business' && showClientTypeFields && (
                <>
                  <div>
                    <Label htmlFor="industry">Industry *</Label>
                    <Input data-testid="lead-form-step3-input-3" id="industry" {...register('industry')} placeholder="e.g., Technology, Healthcare, Retail" className={errors.industry ? 'border-red-500' : ''} />
                    {errors.industry && <p className="text-red-500 text-sm mt-1">{errors.industry.message}</p>}
                  </div>
                  <div>
                    <Label>Annual Revenue *</Label>
                    <Select data-testid="lead-form-step3-select-2" onValueChange={(val) => setValue('annual_revenue', val)}>
                      <SelectTrigger data-testid="lead-form-step3-selecttrigger-2" className={errors.annual_revenue ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select revenue range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem data-testid="lead-form-step3-selectitem-10" value="under_500k">Under $500K</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-9" value="500k_1m">$500K - $1M</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-8" value="1m_5m">$1M - $5M</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-7" value="5m_10m">$5M - $10M</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-6" value="over_10m">Over $10M</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.annual_revenue && <p className="text-red-500 text-sm mt-1">{errors.annual_revenue.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="accounting_software">Current Accounting Software *</Label>
                    <Input data-testid="lead-form-step3-input-2" id="accounting_software" {...register('accounting_software')} placeholder="e.g., QuickBooks, Xero, NetSuite" className={errors.accounting_software ? 'border-red-500' : ''} />
                    {errors.accounting_software && <p className="text-red-500 text-sm mt-1">{errors.accounting_software.message}</p>}
                  </div>
                </>
              )}

              {/* ===== INDIVIDUAL FIELDS ===== */}
              {clientType === 'individual' && (
                <>
                  <div>
                    <Label className="text-base font-semibold">What best describes your situation? *</Label>
                    <RadioGroup onValueChange={(val) => setValue('tax_situation', val)}>
                      <div className="space-y-2 mt-2">
                        {[
                          { value: 'simple_w2', label: 'I file a simple W-2 return' },
                          { value: 'multiple_income', label: 'I have multiple income sources (W-2 + freelance / rental / investments)' },
                          { value: 'irs_issue', label: 'I have an IRS issue I need help with' },
                          { value: 'life_change', label: "I'm going through a life change (marriage, divorce, inheritance, retirement)" },
                          { value: 'self_employed', label: "I'm self-employed / freelancer" },
                          { value: 'other', label: 'Other' },
                        ].map(opt => (
                          <div key={opt.value} className="flex items-center space-x-2">
                            <RadioGroupItem value={opt.value} id={`ts_${opt.value}`} />
                            <label htmlFor={`ts_${opt.value}`} className="cursor-pointer text-sm">{opt.label}</label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                    {errors.tax_situation && <p className="text-red-500 text-sm mt-1">{errors.tax_situation.message}</p>}
                  </div>

                  {taxSituationValue === 'other' && (
                    <div>
                      <Label htmlFor="tax_situation_other">Please describe your situation *</Label>
                      <Input data-testid="lead-form-step3-input-1" id="tax_situation_other" {...register('tax_situation_other')} placeholder="Tell us what you need help with" />
                      {errors.tax_situation_other && <p className="text-red-500 text-sm mt-1">{errors.tax_situation_other.message}</p>}
                    </div>
                  )}

                  <div>
                    <Label className="text-base font-semibold">Filing Status *</Label>
                    <Select data-testid="lead-form-step3-select-1" onValueChange={(val) => setValue('filing_status', val)}>
                      <SelectTrigger data-testid="lead-form-step3-selecttrigger-1" className={errors.filing_status ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select your filing status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem data-testid="lead-form-step3-selectitem-5" value="single">Single</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-4" value="married_joint">Married Filing Jointly</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-3" value="married_separate">Married Filing Separately</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-2" value="head_of_household">Head of Household</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-1" value="widower">Qualifying Widower</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.filing_status && <p className="text-red-500 text-sm mt-1">{errors.filing_status.message}</p>}
                  </div>

                  <div>
                    <Label className="text-base font-semibold">Do you currently have a CPA? *</Label>
                    <RadioGroup onValueChange={(val) => setValue('has_cpa', val)}>
                      <div className="flex gap-6 mt-2">
                        {[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                          { value: 'not_sure', label: 'Not sure' },
                        ].map(opt => (
                          <div key={opt.value} className="flex items-center space-x-2">
                            <RadioGroupItem value={opt.value} id={`cpa_${opt.value}`} />
                            <label htmlFor={`cpa_${opt.value}`} className="cursor-pointer">{opt.label}</label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                    {errors.has_cpa && <p className="text-red-500 text-sm mt-1">{errors.has_cpa.message}</p>}
                  </div>
                </>
              )}

              {/* ===== SHARED FIELDS (show once client_type is selected) ===== */}
              {clientType && (
                <>
                  {/* Services */}
                  <div>
                    <Label className="text-base font-semibold">Services Needed * <span className="text-sm text-gray-500 font-normal">(Select all that apply)</span></Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                      {availableServices.map(service => (
                        <div key={service.value} className="flex items-center space-x-2">
                          <Checkbox data-testid="lead-form-step3-checkbox-2"
                            id={service.value}
                            checked={selectedServices.includes(service.value)}
                            onCheckedChange={() => handleServiceToggle(service.value)}
                          />
                          <label htmlFor={service.value} className="text-sm cursor-pointer">{service.label}</label>
                        </div>
                      ))}
                    </div>
                    {errors.services && <p className="text-red-500 text-sm mt-1">{errors.services.message}</p>}
                  </div>

                  {/* Timeline */}
                  <div>
                    <Label className="text-base font-semibold">When do you need help? *</Label>
                    <RadioGroup onValueChange={(val) => setValue('timeline', val)}>
                      <div className="space-y-2 mt-2">
                        {[
                          { value: 'immediate', label: 'Immediate (within 1-2 weeks)' },
                          { value: 'soon', label: 'Soon (within 1-2 months)' },
                          { value: 'planning', label: 'Planning Ahead (3-6 months)' },
                          { value: 'exploring', label: 'Just Exploring' },
                        ].map(opt => (
                          <div key={opt.value} className="flex items-center space-x-2">
                            <RadioGroupItem value={opt.value} id={`tl_${opt.value}`} />
                            <label htmlFor={`tl_${opt.value}`} className="cursor-pointer">{opt.label}</label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                    {errors.timeline && <p className="text-red-500 text-sm mt-1">{errors.timeline.message}</p>}
                  </div>

                  {/* Values */}
                  <div>
                    <Label className="text-base font-semibold">What values resonate with you? * <span className="text-sm text-gray-500 font-normal">(Select all that apply)</span></Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {VALUES.map(val => (
                        <div key={val.value} className="flex items-center space-x-2">
                          <Checkbox data-testid="lead-form-step3-checkbox-1" id={val.value} checked={selectedValues.includes(val.value)} onCheckedChange={() => handleValueToggle(val.value)} />
                          <label htmlFor={val.value} className="text-sm cursor-pointer">{val.label}</label>
                        </div>
                      ))}
                    </div>
                    {errors.values_fit && <p className="text-red-500 text-sm mt-1">{errors.values_fit.message}</p>}
                  </div>

                  {/* Story */}
                  <div>
                    <Label htmlFor="story" className="text-base font-semibold">Tell us your story * <span className="text-sm text-gray-500 font-normal">(20-1000 characters)</span></Label>
                    <Textarea data-testid="lead-form-step3-textarea-1" id="story" {...register('story')} rows={5} className="resize-none" placeholder="Share what brings you here, your challenges, goals, or questions. The more we know, the better we can help." />
                    <div className="flex justify-between items-center mt-1">
                      {errors.story && <p className="text-red-500 text-sm">{errors.story.message}</p>}
                      <p className={`text-sm ml-auto ${storyLength > 1000 ? 'text-red-500' : storyLength < 20 ? 'text-gray-400' : 'text-green-600'}`}>
                        {storyLength} / 1000
                      </p>
                    </div>
                  </div>

                  {/* Referral Source */}
                  <div>
                    <Label className="text-base font-semibold">How did you hear about us? *</Label>
                    <Select data-testid="lead-form-step3-select-1" onValueChange={(val) => setValue('referral_source', val)}>
                      <SelectTrigger data-testid="lead-form-step3-selecttrigger-1"><SelectValue placeholder="Select source" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem data-testid="lead-form-step3-selectitem-5" value="google">Google Search</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-4" value="linkedin">LinkedIn</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-3" value="referral">Referral from Someone</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-2" value="event">Event / Conference</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-1" value="existing_client">Existing Client</SelectItem>
                        <SelectItem data-testid="lead-form-step3-selectitem-1" value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.referral_source && <p className="text-red-500 text-sm mt-1">{errors.referral_source.message}</p>}
                  </div>

                  {referralSourceValue === 'referral' && (
                    <div>
                      <Label htmlFor="referrer_name">Who referred you? *</Label>
                      <Input data-testid="lead-form-step3-input-1" id="referrer_name" {...register('referrer_name')} placeholder="Name of person who referred you" />
                      {errors.referrer_name && <p className="text-red-500 text-sm mt-1">{errors.referrer_name.message}</p>}
                    </div>
                  )}

                  {/* Consent */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <Checkbox data-testid="lead-form-step3-checkbox-1" id="consent" onCheckedChange={(checked) => setValue('consent', checked)} />
                      <label htmlFor="consent" className="text-sm cursor-pointer leading-relaxed">
                        I consent to being contacted by KGOB CPAs & Advisors regarding my inquiry. I understand that by providing my contact information, I may be reached via phone, email, or other means to discuss services. *
                      </label>
                    </div>
                    {errors.consent && <p className="text-red-500 text-sm mt-2">{errors.consent.message}</p>}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-gold to-gold-600 text-white hover:from-gold-600 hover:to-gold-700 py-6 text-lg"
                    disabled={isSubmitting}
                    data-testid="submit-button"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LeadFormStep3;
